import React from 'react'

import { Box, ModalBody, ModalClose, ModalFooter, ModalHeader } from '@gousto-internal/citrus-react'

import {
  RecipeCoreId,
  RecipeQueryResponse,
} from '@library/api-menu-experience/src/interfaces/recipe'

import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'

import { AddRecipeButton } from './AddRecipeButton'
import { RecipeAllergens } from './Allergens'
import { RecipeTags, RecipeAttributes } from './Attributes'
import { RecipeDescription } from './Description'
import { DetailError } from './DetailError'
import { RecipeEquipment } from './Equipment'
import { RecipeImage } from './Image'
import { RecipeIngredients } from './Ingredients'
import { RecipeNutrition } from './Nutrition'
import { RecipeOptions } from './Options'
import { Title } from './Title'

type DetailProps = {
  recipe: RecipeQueryResponse
  recipeCoreId: RecipeCoreId
  position?: string
  loading: boolean
  error: boolean
  onClose: () => void
  reload: () => void
}

export const DetailContent = ({
  recipe,
  recipeCoreId,
  position,
  loading,
  error,
  onClose,
  reload,
}: DetailProps) => {
  const {
    title,
    images,
    equipment,
    displayType,
    customisations,
    id: recipeUuid,
    description,
    ingredients: fullIngredients,
    nutrition,
    pricing,
    allergens,
    tagGroups,
  } = recipe || {}

  const { included: ingredients, notIncluded: ingredientsNotIncluded } = fullIngredients || {}

  const keyTagGroups = tagGroups?.find((group) => group.group === 'key')
  const generalTagGroups = tagGroups?.find((group) => group.group === 'general')

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <Title data-testid="recipe-detail-title" data-testing="recipeDetailTitle">
          {title}
        </Title>
      </ModalHeader>
      <ModalBody>
        <Box>
          <RecipeImage images={images} displayType={displayType} />
          <SectionPanel data-testid="recipe-detail-section-panel">
            <RecipeOptions customisations={customisations} recipeUuid={recipeUuid} />
            <RecipeTags tags={keyTagGroups?.tags} />
            <RecipeDescription description={description} loading={loading} />
            <RecipeAttributes
              tags={generalTagGroups?.tags}
              loading={loading}
              data-testid="recipe-detail-attributes"
            />
          </SectionPanel>
          {error ? (
            <DetailError reload={reload} />
          ) : (
            <>
              <RecipeIngredients
                ingredients={ingredients}
                ingredientsNotIncluded={ingredientsNotIncluded}
                loading={loading}
              />
              <RecipeEquipment equipment={equipment} onClose={onClose} loading={loading} />
              <RecipeNutrition nutritionalInformation={nutrition} loading={loading} />
              <RecipeAllergens
                allergens={allergens}
                includedIngredients={ingredients}
                loading={loading}
              />
            </>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <AddRecipeButton
          id={recipeUuid}
          coreId={recipeCoreId}
          pricing={pricing}
          position={position}
        />
      </ModalFooter>
    </>
  )
}
